import { useEffect, useRef } from "react";
import { canvasDpr } from "@/lib/canvas";

const CYAN = "0, 229, 255";

const makeParticle = (w, h) => {
  const vx = (Math.random() - 0.5) * 0.35;
  const vy = (Math.random() - 0.5) * 0.35;
  return { x: Math.random() * w, y: Math.random() * h, vx, vy, bx: vx, by: vy, r: Math.random() * 0.9 + 0.5 };
};

export const BackgroundNetwork = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mouse = { x: -9999, y: -9999, active: false };
    let w = 0, h = 0, particles = [], raf = null, resizeTimer = null;

    const resize = () => {
      const dpr = canvasDpr();
      const nw = window.innerWidth;
      const nh = window.innerHeight;
      if (particles.length && w) particles.forEach((p) => { p.x *= nw / w; p.y *= nh / h; });
      const widthChanged = nw !== w;
      w = nw;
      h = nh;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (widthChanged) {
        const mobile = w < 768;
        const count = Math.round(Math.min(mobile ? 45 : 130, (w * h) / (mobile ? 8500 : 11000)));
        particles = Array.from({ length: count }, () => makeParticle(w, h));
      }
    };

    const update = (p) => {
      if (mouse.active) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d = Math.hypot(dx, dy) || 1;
        if (d < 110) {
          const f = (1 - d / 110) * 0.6;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        } else if (d < 240) {
          const f = (1 - (d - 110) / 130) * 0.012;
          p.vx -= (dx / d) * f;
          p.vy -= (dy / d) * f;
        }
      }
      p.vx += (p.bx - p.vx) * 0.03;
      p.vy += (p.by - p.vy) * 0.03;
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < -20) p.x = w + 20;
      else if (p.x > w + 20) p.x = -20;
      if (p.y < -20) p.y = h + 20;
      else if (p.y > h + 20) p.y = -20;
    };

    const line = (ax, ay, bx, by, a) => {
      ctx.strokeStyle = `rgba(${CYAN}, ${a})`;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const link = w < 768 ? 110 : 150;
      const n = particles.length;
      ctx.lineWidth = 0.5;
      for (let i = 0; i < n; i++) {
        const a = particles[i];
        for (let j = i + 1; j < n; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          if (Math.abs(dx) > link || Math.abs(dy) > link) continue;
          const d = Math.hypot(dx, dy);
          if (d < link) line(a.x, a.y, b.x, b.y, (1 - d / link) * 0.55);
        }
        if (mouse.active) {
          const d = Math.hypot(a.x - mouse.x, a.y - mouse.y);
          if (d < 200) line(a.x, a.y, mouse.x, mouse.y, (1 - d / 200) * 0.6);
        }
      }
      for (let i = 0; i < n; i++) {
        const p = particles[i];
        ctx.fillStyle = `rgba(${CYAN}, 0.14)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${CYAN}, 1)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = () => {
      particles.forEach(update);
      draw();
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (reduce || raf || document.hidden) return;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };

    const setPointer = (x, y) => {
      mouse.active = true;
      mouse.x = x;
      mouse.y = y;
    };
    const onMouseMove = (e) => setPointer(e.clientX, e.clientY);
    const onTouch = (e) => e.touches[0] && setPointer(e.touches[0].clientX, e.touches[0].clientY);
    const onLeave = () => {
      mouse.active = false;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resize();
        if (reduce) draw();
      }, 150);
    };

    resize();
    if (reduce) draw();
    else start();

    if (!reduce) {
      window.addEventListener("mousemove", onMouseMove, { passive: true });
      window.addEventListener("touchstart", onTouch, { passive: true });
      window.addEventListener("touchmove", onTouch, { passive: true });
      window.addEventListener("touchend", onLeave, { passive: true });
      document.addEventListener("mouseleave", onLeave);
    }
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchstart", onTouch);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("touchend", onLeave);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 block pointer-events-none"
      style={{ zIndex: -1, opacity: 0.35 }}
      aria-hidden="true"
      data-testid="bg-particle-canvas"
    />
  );
};
